import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Megaphone, Trash2, Star, Mail, Bell } from "lucide-react";

export default function NewsView({ token }) {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const empty = { title: "", body: "", link: "", important: false, notify: true, email: false };
  const [form, setForm] = useState(empty);
  const [list, setList] = useState([]);
  const [sending, setSending] = useState(false);
  const load = () => axios.get(`${API}/superadmin/news`, h).then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Titre et message requis");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/superadmin/news`, form, h);
      toast.success(`Annonce publiée${data.recipients ? ` — ${data.recipients} admin(s) notifié(s)` : ""}`);
      setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSending(false); }
  };
  const remove = async (id) => { try { await axios.delete(`${API}/superadmin/news/${id}`, h); load(); } catch { toast.error("Suppression impossible"); } };
  const Check = ({ k, icon: Icon, label, desc }) => (
    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-zinc-800 bg-zinc-900/50 p-3 transition-colors hover:border-zinc-700">
      <input type="checkbox" data-testid={`news-${k}`} checked={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.checked }))} className="mt-0.5 h-4 w-4 accent-[#FF6A00]" />
      <span><span className="flex items-center gap-1.5 text-sm font-medium text-white"><Icon className="h-4 w-4 text-[#FF6A00]" />{label}</span><span className="mt-0.5 block text-xs text-zinc-500">{desc}</span></span>
    </label>
  );
  return (
    <div className="max-w-2xl space-y-6" data-testid="sa-news">
      <div>
        <h2 className="flex items-center gap-2 font-display text-xl font-bold text-white"><Megaphone className="h-5 w-5 text-[#FF6A00]" />Annonces</h2>
        <p className="mt-1 text-sm text-zinc-500">Diffusez une actualité à tous les administrateurs des prestataires.</p>
      </div>
      <div className="space-y-3 rounded-2xl border border-zinc-800 bg-zinc-950 p-5">
        <div><Label className="text-zinc-400">Titre</Label><Input data-testid="news-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-900" placeholder="Ex : Nouvelle fonctionnalité disponible" /></div>
        <div><Label className="text-zinc-400">Message</Label><Textarea data-testid="news-body" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-900" /></div>
        <div><Label className="text-zinc-400">Lien (optionnel)</Label><Input data-testid="news-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="mt-1 border-zinc-800 bg-zinc-900" placeholder="/app/parametres" /></div>
        <div className="grid gap-2 sm:grid-cols-3">
          <Check k="notify" icon={Bell} label="Notifier dans la cloche" desc="Pousse l'annonce dans la sidebar de notifications" />
          <Check k="important" icon={Star} label="Importante" desc="Surlignée en orange dans la sidebar" />
          <Check k="email" icon={Mail} label="Envoyer par email" desc="Envoie aussi un email à chaque admin" />
        </div>
        <Button data-testid="news-submit" onClick={submit} disabled={sending} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Megaphone className="mr-1.5 h-4 w-4" />Publier l'annonce</Button>
      </div>
      <div className="space-y-2">
        {list.length === 0 ? <p className="text-sm text-zinc-500">Aucune annonce publiée.</p> : list.map((n) => (
          <div key={n.id} data-testid={`news-row-${n.id}`} className="flex items-start justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-900 p-4">
            <div>
              <p className="flex items-center gap-2 font-medium text-white">{n.important && <Star className="h-3.5 w-3.5 text-[#FF6A00]" />}{n.title}</p>
              <p className="mt-0.5 text-sm text-zinc-400">{n.body}</p>
              <p className="mt-1 text-xs text-zinc-600">{new Date(n.created_at).toLocaleString("fr-FR")}{n.notify ? " · cloche" : ""}{n.email ? " · email" : ""}</p>
            </div>
            <button data-testid={`news-del-${n.id}`} onClick={() => remove(n.id)} className="rounded-full p-2 text-zinc-500 hover:bg-red-500/10 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
